// A casca do menu de contexto: onde ele aparece, como se fecha.
//
// O CONTEÚDO não mora aqui — ele é o mesmo `renderMenuActionItem` que desenha a listinha do
// botão de três pontos hoje, e chega pronto em `itens`. Uma lista só (Regra #9): o menu do
// clique direito e o do ⋮ não podem divergir, e a única forma de garantir isso é não haver
// duas fontes.
//
// A casca também é emprestada: `createPopup` é o que o `popMenu` do data-view usa por baixo —
// posicionamento por floating-ui contra um retângulo qualquer, camada modal própria, e
// fechamento no clique fora. É o mesmo mecanismo que a linha da tabela do banco de dados usa
// no `contextmenu` dela.

import { createPopup } from '@blocksuite/affine-components/context-menu';
import { renderToolbarSeparator } from '@blocksuite/affine-components/toolbar';
import { html, render, type TemplateResult } from 'lit';
import { join } from 'lit/directives/join.js';

import { retanguloNoPonto } from './decidir-menu-de-contexto';

/**
 * Abre o menu do elemento no ponto do ponteiro.
 *
 * @returns função que fecha o menu.
 */
export function abrirMenuNoPonto({
  x,
  y,
  raiz,
  itens,
}: {
  /** Coordenadas de CLIENTE — as do próprio evento. */
  x: number;
  y: number;
  /**
   * Onde a camada modal é pendurada. Precisa ser o host do editor, e não `document.body`: as
   * variáveis de tema (`--affine-*`) e as definições dos `editor-menu-*` vivem sob ele.
   */
  raiz: HTMLElement;
  itens: TemplateResult[];
}): () => void {
  const conteudo = document.createElement('editor-menu-content');
  conteudo.setAttribute('role', 'menu');
  conteudo.setAttribute('tabindex', '-1');
  // ⚠️ `editor-menu-content` nasce com `display: none` e só aparece com `[data-show]` — é o
  // `createButtonPopper` que o marca quando o botão de três pontos abre. Aqui não há botão, e
  // sem esta linha o menu é montado, posicionado, e NÃO aparece: nenhum erro, nada na tela.
  conteudo.dataset.show = '';
  // O `createPopup` escreve `left`/`top` a cada reposicionamento; sem `position: absolute` o
  // elemento ignora os dois e o menu nasce no fim do modal, não no ponteiro.
  conteudo.style.position = 'absolute';

  // ⚠️ `data-size` e `data-orientation` valem no FILHO, não no host: as regras são `::slotted`.
  // É a mesma estrutura que o botão de três pontos monta hoje.
  render(
    html`<div data-size="large" data-orientation="vertical">
      ${join(itens, renderToolbarSeparator('horizontal'))}
    </div>`,
    conteudo
  );

  // ⚠️ O menu flutua DENTRO do host do editor, e o canvas escuta ponteiro no host. Sem isto,
  // o clique num item do menu é lido pela ferramenta como clique no vazio do quadro — e
  // `DefaultTool.click` responde a isso com `selection.clear()`. A seleção morre no
  // `pointerdown`, milissegundos antes de o `run()` da ação ler `getSurfaceModels()`.
  //
  // Medido no `dev` em 2026-08-28: o clique CHEGA ao item (ouvinte próprio disparou), e no
  // mesmo instante `elementsMap$.size` era 0 — tendo sido 1 com o menu aberto e parado.
  //
  // O menu é interface flutuante, não gesto de canvas: os eventos dele param aqui.
  const PARAR = [
    'pointerdown',
    'pointermove',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'dblclick',
  ] as const;
  PARAR.forEach(nome =>
    conteudo.addEventListener(nome, e => e.stopPropagation())
  );

  const rect = retanguloNoPonto(x, y);

  const fechar = createPopup(
    {
      targetRect: { getBoundingClientRect: () => rect as DOMRect },
      root: raiz,
      // Este alvo é um ponto, não um elemento: não há botão para marcar como ativo enquanto o
      // menu está aberto, então não há nada a desfazer no fim.
      popupStart: () => () => {},
    },
    conteudo,
    { placement: 'bottom-start' }
  );

  // O `createPopup` fecha no clique fora e no clique direito fora, mas não escuta teclado.
  // Sem isto, o `Escape` do menu chega ao canvas e limpa a seleção com o menu ainda aberto.
  const aoTeclar = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    fecharTudo();
  };

  // Um clique num item age e deve fechar. Os itens são `editor-menu-action`, que não sabem do
  // menu que os hospeda — quem fecha é quem abriu.
  const aoClicar = () => fecharTudo();

  const fecharTudo = () => {
    document.removeEventListener('keydown', aoTeclar, true);
    conteudo.removeEventListener('click', aoClicar);
    fechar();
  };

  document.addEventListener('keydown', aoTeclar, true);
  conteudo.addEventListener('click', aoClicar);

  return fecharTudo;
}
